import { useState, useCallback, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import "@/App.css";

import SiteHeader from "@/components/SiteHeader";
import Hero from "@/components/Hero";
import Intro from "@/components/Intro";
import AwakeLoop from "@/components/AwakeLoop";
import Footer from "@/components/Footer";
import AttentionStep from "@/components/wizard/AttentionStep";
import WitnessStep from "@/components/wizard/WitnessStep";
import AlignStep from "@/components/wizard/AlignStep";
import KataStep from "@/components/wizard/KataStep";
import EvolveStep from "@/components/wizard/EvolveStep";
import PlanOutput from "@/components/PlanOutput";
import LearnMoreCTA from "@/components/LearnMoreCTA";
import WorkshopTimer from "@/components/WorkshopTimer";
import Facilitate from "@/pages/Facilitate";
import Join from "@/pages/Join";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ProtectedRoute from "@/components/ProtectedRoute";
import { MemberstackProvider } from "@/lib/memberstack";
import { Toaster } from "@/components/ui/sonner";

const STAGES = [
  "landing",
  "attention",
  "witness",
  "align",
  "kata",
  "evolve",
  "output",
];

const STAGE_TO_LOOP_INDEX = {
  attention: 0,
  witness: 1,
  align: 2,
  kata: 3,
  evolve: 4,
  output: 4,
};

function MainApp() {
  const [stage, setStage] = useState("landing");
  const [firstName, setFirstName] = useState("");
  const [contextText, setContextText] = useState("");
  const [contextLabel, setContextLabel] = useState("");
  const [attention, setAttention] = useState({
    working_on: "",
    assumptions: "",
    evidence: "",
    might_be_missing: "",
  });
  const [witness, setWitness] = useState({
    who_for: "",
    frustrates: "",
    doing_instead: "",
    feel_but_not_say: "",
  });
  const [refinedProblem, setRefinedProblem] = useState("");
  const [kataHistory, setKataHistory] = useState([]);
  const [plan, setPlan] = useState(null);

  // Smooth scroll to top of wizard area when stage changes
  useEffect(() => {
    if (stage !== "landing") {
      const node = document.getElementById("awake-wizard");
      if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [stage]);

  const startJourney = useCallback(() => setStage("attention"), []);
  const goTo = useCallback((s) => setStage(s), []);

  const reset = useCallback(() => {
    setFirstName("");
    setContextText("");
    setContextLabel("");
    setAttention({
      working_on: "",
      assumptions: "",
      evidence: "",
      might_be_missing: "",
    });
    setWitness({
      who_for: "",
      frustrates: "",
      doing_instead: "",
      feel_but_not_say: "",
    });
    setRefinedProblem("");
    setKataHistory([]);
    setPlan(null);
    setStage("landing");
  }, []);

  return (
    <div className="App" data-testid="app-root">
      <SiteHeader onLogoClick={reset} />

      {stage === "landing" && (
        <>
          <Hero onStart={startJourney} />
          <Intro />
          <AwakeLoop activeIndex={-1} />
          <div className="max-w-3xl mx-auto px-6 pb-24 text-center">
            <button
              type="button"
              onClick={startJourney}
              className="weave-btn-primary"
              data-testid="bottom-start-btn"
            >
              Start your AWAKE journey
            </button>
            <p className="mt-5 text-sm text-slate-500">
              Already done the work?{" "}
              <Link
                to="/join"
                className="font-medium text-[#3c8386] hover:text-[#2f6e71] underline decoration-[#4aa0a4]/40 underline-offset-2 hover:decoration-[#3c8386] transition-colors"
                data-testid="landing-join-link"
              >
                Step inside the 1% Club →
              </Link>
            </p>
          </div>
          <div className="max-w-3xl mx-auto px-6 pb-32">
            <LearnMoreCTA />
          </div>
          <Footer />
        </>
      )}

      {stage !== "landing" && (
        <main id="awake-wizard" className="min-h-screen">
          <div className="weave-timer-float" data-testid="floating-timer">
            <WorkshopTimer />
          </div>

          <div className="max-w-3xl mx-auto px-6 pt-28 pb-2 flex items-center justify-between gap-3 flex-wrap">
            <button
              type="button"
              className="weave-btn-outline"
              onClick={reset}
              data-testid="restart-btn"
            >
              ← Start over
            </button>
          </div>

          <div className="max-w-3xl mx-auto px-6">
            <AwakeLoop activeIndex={STAGE_TO_LOOP_INDEX[stage]} />
          </div>

          <section className="max-w-3xl mx-auto px-6 pb-16 pt-4">
            {stage === "attention" && (
              <AttentionStep
                value={attention}
                onChange={setAttention}
                firstName={firstName}
                onFirstNameChange={setFirstName}
                contextText={contextText}
                contextLabel={contextLabel}
                onSetContext={({ text, label }) => {
                  setContextText(text);
                  setContextLabel(label);
                }}
                onClearContext={() => {
                  setContextText("");
                  setContextLabel("");
                }}
                onNext={() => goTo("witness")}
              />
            )}
            {stage === "witness" && (
              <WitnessStep
                value={witness}
                onChange={setWitness}
                onBack={() => goTo("attention")}
                onNext={() => goTo("align")}
              />
            )}
            {stage === "align" && (
              <AlignStep
                attention={attention}
                witness={witness}
                contextText={contextText}
                refinedProblem={refinedProblem}
                onRefinedChange={setRefinedProblem}
                onBack={() => goTo("witness")}
                onNext={() => goTo("kata")}
              />
            )}
            {stage === "kata" && (
              <KataStep
                attention={attention}
                witness={witness}
                contextText={contextText}
                refinedProblem={refinedProblem}
                history={kataHistory}
                setHistory={setKataHistory}
                onBack={() => goTo("align")}
                onNext={() => goTo("evolve")}
              />
            )}
            {stage === "evolve" && (
              <EvolveStep
                attention={attention}
                witness={witness}
                contextText={contextText}
                refinedProblem={refinedProblem}
                kataHistory={kataHistory}
                onPlanReady={(p) => {
                  setPlan(p);
                  goTo("output");
                }}
                onBack={() => goTo("kata")}
              />
            )}
            {stage === "output" && plan && (
              <PlanOutput
                plan={plan}
                refinedProblem={refinedProblem}
                attention={attention}
                witness={witness}
                firstName={firstName}
                onRestart={reset}
              />
            )}
          </section>
          <Footer />
        </main>
      )}
    </div>
  );
}

function App() {
  return (
    <MemberstackProvider>
      <Toaster position="bottom-center" />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainApp />} />
          <Route path="/facilitate" element={<Facilitate />} />
          <Route path="/join" element={<Join />} />
          <Route path="/login" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </MemberstackProvider>
  );
}

export default App;
